import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api, { authAPI, setAccessToken, clearAccessToken } from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem('user');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(false);
  const [gdprConsented, setGdprConsented] = useState(
    () => sessionStorage.getItem('gdpr_consented') === 'true'
  );

  // ── Verificare & reîmprospătare sesiune în fundal ─────────────
  useEffect(() => {
    let storedUser = null;
    try { storedUser = JSON.parse(localStorage.getItem('user')); } catch {}
    
    api.post('/auth/refresh', { userId: storedUser?.id })
      .then(({ data }) => {
        setAccessToken(data.accessToken);
        setUser(data.user);
        const { cnp, ...safeUser } = data.user || {};
        localStorage.setItem('user', JSON.stringify(safeUser));
      })
      .catch((err) => {
        // Dacă serverul confirmă explicit că tokenul a expirat complet / nu e autorizat
        if (err.response?.status === 401) {
          clearAccessToken();
          setUser(null);
          localStorage.removeItem('user');
        }
      });
  }, []);

  const acceptGdpr = useCallback(() => {
    sessionStorage.setItem('gdpr_consented', 'true');
    setGdprConsented(true);
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await authAPI.login({ email, password });
    if (data.requiresOTP) return { requiresOTP: true, userId: data.userId };
    sessionStorage.removeItem('gdpr_consented');
    setGdprConsented(false);
    saveSession(data);
    return { success: true };
  }, []);

  const verifyOTP = useCallback(async (userId, otp) => {
    const { data } = await authAPI.verifyOTP({ userId, otp });
    sessionStorage.removeItem('gdpr_consented');
    setGdprConsented(false);
    saveSession(data);
    return { success: true };
  }, []);

  // Apelat după confirmarea emailului — salvează sesiunea și returnează dacă profilul e complet
  const saveVerifiedSession = useCallback((data) => {
    saveSession(data);
    return data.user?.profil_completat === false;
  }, []);

  // Actualizare user după completare profil
  const updateUser = useCallback((updatedUser) => {
    setUser(prev => {
      const merged = { ...prev, ...updatedUser };
      const { cnp, ...safeUser } = merged;
      localStorage.setItem('user', JSON.stringify(safeUser));
      return merged;
    });
  }, []);

  const loginWithEudi = useCallback((data) => {
    sessionStorage.removeItem('gdpr_consented');
    setGdprConsented(false);
    saveSession(data);
  }, []);

  const logout = useCallback(async () => {
    try { await authAPI.logout(); } catch {}
    clearAccessToken();
    setUser(null);
    localStorage.removeItem('user');
    sessionStorage.removeItem('gdpr_consented');
    setGdprConsented(false);
  }, []);

  function saveSession(data) {
    setAccessToken(data.accessToken);
    // Persistă datele non-sensitive ale userului (fără CNP) pentru UI
    const { cnp, ...safeUser } = data.user || {};
    localStorage.setItem('user', JSON.stringify(safeUser));
    setUser(data.user);
  }

  const isFunctionar = user && ['functionar','sef_departament','secretar_general','admin','primar','superadmin'].includes(user.rol);
  const isAdmin      = user && ['admin','primar','superadmin'].includes(user.rol);
  const needsProfile = user && user.rol === 'cetatean' && user.profil_completat === false;

  return (
    <AuthContext.Provider value={{ user, loading, login, verifyOTP, saveVerifiedSession, updateUser, loginWithEudi, logout, isFunctionar, isAdmin, needsProfile, gdprConsented, acceptGdpr }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth trebuie folosit în AuthProvider');
  return ctx;
};
